// Builds a public /openmaps deep link for a spot/sesh/event and shares it
// outside the app (Web Share API, falling back to clipboard copy on
// platforms without it - e.g. desktop browsers). Used by the "Share
// elsewhere" button on ShareSpotPage/ShareJamSeshPage/ShareEventPage,
// alongside (not replacing) the existing in-app "send to a user" flow.
// The recipient lands on sections/OpenMapsPage.tsx, which reads this same
// query param to center on the right pin.
export const OPEN_MAPS_URL = 'https://ridaz.app/openmaps';

export type ShareLinkKind = 'spot' | 'sesh' | 'event' | 'shop';

export const buildOpenMapsLink = (kind: ShareLinkKind, id: string) =>
  `${OPEN_MAPS_URL}?${kind}=${encodeURIComponent(id)}`;

export type ShareElsewhereResult = 'shared' | 'copied' | 'unavailable';

export async function shareElsewhere(params: {
  kind: ShareLinkKind;
  id: string;
  title: string;
  text?: string;
}): Promise<ShareElsewhereResult> {
  const url = buildOpenMapsLink(params.kind, params.id);

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: params.title, text: params.text, url });
      return 'shared';
    } catch (err) {
      // User dismissed the native share sheet - not an error, nothing to fall back to.
      if ((err as DOMException)?.name === 'AbortError') return 'shared';
      // Any other failure (e.g. share API present but blocked) - fall through to clipboard.
    }
  }

  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch {
      return 'unavailable';
    }
  }

  return 'unavailable';
}

// --- Marketplace listings ---------------------------------------------------
// ridaz.app/marketplace?listing=<id> is rewritten to the marketplaceShare Cloud
// Function (see firebase.json), which serves the SPA with the listing's photo +
// price as the link preview. The app reads the same query param to open it.
export const MARKETPLACE_URL = 'https://ridaz.app/marketplace';
export const buildListingLink = (id: string) => `${MARKETPLACE_URL}?listing=${encodeURIComponent(id)}`;

export async function shareListing(params: { id: string; title: string; text?: string }): Promise<ShareElsewhereResult> {
  const url = buildListingLink(params.id);
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: params.title, text: params.text, url });
      return 'shared';
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return 'shared';
    }
  }
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(url);
      return 'copied';
    } catch {
      return 'unavailable';
    }
  }
  return 'unavailable';
}
